import type { FlamioConfig, RecordingPrivacyRules } from './types';

export function emptyRecordingPrivacyRules(): RecordingPrivacyRules {
  return {
    maskSelectors: [],
    blockSelectors: [],
    urlAllowlist: [],
    urlBlocklist: [],
  };
}

function cleanList(raw: string[] | undefined): string[] {
  if (!Array.isArray(raw)) return [];
  return Array.from(
    new Set(
      raw
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
    )
  );
}

export function rulesFromConfig(config: FlamioConfig): RecordingPrivacyRules {
  return {
    maskSelectors: cleanList(config.maskSelectors),
    blockSelectors: cleanList(config.blockSelectors),
    urlAllowlist: cleanList(config.urlAllowlist),
    urlBlocklist: cleanList(config.urlBlocklist),
  };
}

// `*` is the only wildcard (→ `.*`); all other chars are literal (metacharacters
// escaped, so no ReDoS). Case-insensitive substring match.
export function urlMatchesPattern(url: string, pattern: string): boolean {
  const trimmed = pattern.trim();
  if (!trimmed) return false;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, (ch) =>
    ch === '*' ? '.*' : `\\${ch}`
  );
  try {
    return new RegExp(escaped, 'i').test(url);
  } catch {
    return false;
  }
}

export function urlMatchesAny(url: string, patterns: string[]): boolean {
  return patterns.some((pattern) => urlMatchesPattern(url, pattern));
}

// Should this URL be recorded? Blocklist wins; a non-empty allowlist means
// "only these URLs".
export function shouldRecordUrl(
  url: string,
  rules: RecordingPrivacyRules
): boolean {
  if (rules.urlBlocklist.length > 0 && urlMatchesAny(url, rules.urlBlocklist)) {
    return false;
  }
  if (
    rules.urlAllowlist.length > 0 &&
    !urlMatchesAny(url, rules.urlAllowlist)
  ) {
    return false;
  }
  return true;
}

export interface UrlGateCallbacks {
  /** URL became recordable (or is at init): start/resume rrweb emission. */
  enter(): void;
  /** URL is not recordable: stop rrweb emission. */
  leave(): void;
}

/**
 * Gates recording by URL. Evaluates `shouldRecordUrl` at init and on every SPA
 * navigation, calling `enter`/`leave` only on transitions so rrweb is never
 * double-started. Pure of the DOM router: the host feeds URLs in.
 */
export class UrlGate {
  private recordable: boolean;

  constructor(
    private readonly rules: RecordingPrivacyRules,
    private readonly cb: UrlGateCallbacks,
    initialUrl: string
  ) {
    this.recordable = shouldRecordUrl(initialUrl, this.rules);
  }

  /** True when recording is currently allowed for the last evaluated URL. */
  isRecordable(): boolean {
    return this.recordable;
  }

  /** Re-evaluate for a new URL, firing enter/leave only on a state change. */
  update(url: string): void {
    const next = shouldRecordUrl(url, this.rules);
    if (next === this.recordable) return;
    this.recordable = next;
    if (next) this.cb.enter();
    else this.cb.leave();
  }
}
